import { EventBus } from '@hcie/shared';
// We import from the UI package to access rendering functions
import { renderLayers, updateLayerPanel } from '@hcie/canvas-ui';

// Sub-coordinators
import { initDocumentCoordinator } from './coordinators/document-coordinator';
import { initToolCoordinator } from './coordinators/tool-coordinator';
import { handleEyeDropper } from './coordinators/eye-dropper-coordinator';
import { initSelectionCoordinator } from './coordinators/selection-coordinator';
import { initOverlayCoordinator } from './coordinators/overlay-coordinator';
import { extendHistoryManager, patchWindowHistoryManager, getHistoryForDoc } from './coordinators/history-coordinator';
import { bindDrawActionToWindow } from './coordinators/draw-action';
import { VectorToolManager } from './coordinators/vector-tool-manager';
import { initVectorSelectCoordinator } from './coordinators/vector-select-coordinator';
import { historyManager, g, Tool } from '@hcie/core';
import { renderVectorShapesToCanvas } from '@hcie/shared';

// Layer Naming Service (#1325)
import { initSmartLayerNaming } from './services/layer-naming-service';
import { initColorPaletteService } from './services/color-palette-service';

// Viewer Mode (#2059)
import { initViewerCoordinator } from './viewer/viewer-coordinator';

// Adjustments/Filters (#2061)
import { initFilterCoordinator } from './coordinators/filter-coordinator';

// Mobile UI (#2074)
import { initMobileUICoordinator } from './coordinators/mobile-ui-coordinator';

// Advanced Color Picker (#2074-AdvancedColor)
import { initAdvancedColorCoordinator } from './coordinators/advanced-color-coordinator';

// Persistence Coordinator (#2095)
import { initPersistenceCoordinator } from './coordinators/persistence-coordinator';
import { PlatformService } from './services/platform-service';

// Path Node Editor (#2055-A)
import { initPathEditCoordinator } from './coordinators/path-edit-coordinator';

// Global types for Window and application state
import './types';

/**
 * @function initUICoordinator
 * @description Initializes all UI event listeners, bridge logic, and sub-coordinators.
 * It sets up the communication between core logic and UI components via EventBus.
 * @returns {void}
 */
export function initUICoordinator(): void {
    console.log('[UI-Coordinator] Initializing subscriptions...');

    // [CRITICAL_LOGIC_START]
    // STEP 1: Runtime Patching
    // PATCH: Register RedEye tool at runtime (as @hcie/core is strictly read-only)
    if (!(Tool as any).RedEye) {
        (Tool as any).RedEye = new Tool('Red Eye Removal', 'btn-red-eye', true);
    }
    
    // Force initial canvas ref lookups
    if (window.initCanvasRefs) window.initCanvasRefs();
    if (window.attachCanvasEvents) window.attachCanvasEvents();

    // STEP 2: Settings Bridge
    // Bridge window.settingsManager.settings → g.settings so canvas-ui can read them
    const settingsManager = window.settingsManager;
    if (settingsManager) {
        (g as any).settings = settingsManager.settings;
        // Persisted global toggles mapped to core flat state
        (g as any).shape_fill = !!settingsManager.get?.('vector.fillShape');
        (g as any).imageSmoothingEnabled = settingsManager.get?.('canvas.imageSmoothing') !== false;
        
        window.addEventListener('hcie-settings-changed', (event: any) => {
            (g as any).settings = event.detail;
            (g as any).shape_fill = !!(event.detail?.vector?.fillShape);
            (g as any).imageSmoothingEnabled = event.detail?.canvas?.imageSmoothing !== false;
        });
    }

    // STEP 3: Core Extensions
    extendHistoryManager(historyManager);
    window.historyManager = historyManager;
    window.g = g;
    window.Tool = Tool;
    bindDrawActionToWindow();
    window.renderVectorShapesToCanvas = renderVectorShapesToCanvas;
    window.handleEyeDropper = handleEyeDropper;
    // [CRITICAL_LOGIC_END]

    // STEP 4: Services and Coordinators
    initSmartLayerNaming();
    initColorPaletteService();
    initDocumentCoordinator();
    initToolCoordinator();
    initSelectionCoordinator();
    initOverlayCoordinator();
    initVectorSelectCoordinator();
    
    // Viewer Mode (#2059) - Desktop only
    if (PlatformService.isTauri()) {
        initViewerCoordinator();
    } else {
        // Hide Viewer Mode menu item on web
        const viewerMenuItem = document.querySelector('[onclick="toggleViewerMode()"]');
        if (viewerMenuItem) (viewerMenuItem as HTMLElement).style.display = 'none';
    }

    initFilterCoordinator();
    initMobileUICoordinator();
    initAdvancedColorCoordinator();
    initPersistenceCoordinator();
    initPathEditCoordinator(); // #2055-A — SVG Path Node Editor

    // PHASE 1 FIX: Expose Core Singletons to window for global coordination
    // This is essential for legacy UI scripts and cross-package history management
    (window as any).g = g;
    (window as any).layers = layers;
    (window as any).historyManager = historyManager; // The core Proxy

    // BUG FIX: Restore History Manager patching (#UndoRedoFix)
    patchWindowHistoryManager();

    // STEP 5: Vector Tool Manager Initialization
    // Restore vector ghost edit manager (DOM overlay)
    const currentVectorToolManager = window.vectorToolManager;
    const isVectorToolManagerRequired: boolean =
        !currentVectorToolManager ||
        typeof currentVectorToolManager !== 'object' ||
        typeof currentVectorToolManager.commitEdit !== 'function' ||
        typeof currentVectorToolManager.cancelEdit !== 'function' ||
        typeof currentVectorToolManager.updateOverlay !== 'function' ||
        typeof currentVectorToolManager.startNewFromTool !== 'function';

    if (isVectorToolManagerRequired) {
        window.vectorToolManager = new VectorToolManager();
    }

    // STEP 6: Tool Specific Implementation Details
    const drawingCanvas = document.getElementById('drawingCanvas') as HTMLCanvasElement;
    if (drawingCanvas) {
        // Direct event binding replaced by event-dispatcher.ts centralization
    }

    // ─── Rendering Signals ───────────────────────────────────

    EventBus.on('RENDER_LAYERS', () => {
        renderLayers();
    });

    EventBus.on('UPDATE_LAYER_PANEL', () => {
        updateLayerPanel();
    });

    // ─── Project IO Signals ──────────────────────────────────

    EventBus.on('PROJECT_LOADED', (_data: any) => {
        renderLayers();
        updateLayerPanel();

        // Mark document as clean (#2069)
        const activeDocument = g.documents[g.activeDocumentIndex] as any;
        if (activeDocument) {
            activeDocument.modified = false;
            EventBus.emit('DOCUMENT_TABS_CHANGED');
        }

        // #2064: Reset the per-document history on load and re-point window.historyManager
        if (activeDocument) {
            const docHistory = getHistoryForDoc(activeDocument.id || String(g.activeDocumentIndex));
            docHistory.clear();
            (window as any).historyManager = docHistory;
            EventBus.emit('HISTORY_CHANGED');
        } else if (window.historyManager) {
            window.historyManager.clear();
        }
    });

    EventBus.on('SETTINGS_LOADED', () => {
        // Sync UI Sliders with loaded global state
        const sizeSlider = document.getElementById('sizeSlider') as HTMLInputElement;
        const sizeValue = document.getElementById('sizeValue');
        const opacitySlider = document.getElementById('opacitySlider') as HTMLInputElement;
        const opacityValue = document.getElementById('opacityValue');
        const hardnessSlider = document.getElementById('hardnessSlider') as HTMLInputElement;
        const hardnessValue = document.getElementById('hardnessValue');

        if (sizeSlider && sizeValue) {
            sizeSlider.value = g.pen_width.toString();
            sizeValue.textContent = g.pen_width + 'px';
        }
        if (opacitySlider && opacityValue) {
            const o = Math.round(g.pen_opacity * 100);
            opacitySlider.value = o.toString();
            opacityValue.textContent = o + '%';
        }
        if (hardnessSlider && hardnessValue) {
            const h = Math.round(g.brush_hardness * 100);
            hardnessSlider.value = h.toString();
            hardnessValue.textContent = h + '%';
        }
    });

    EventBus.on('PROJECT_RESIZE', (data: { width: number; height: number }) => {
        if (window.resizeCanvas) {
            window.resizeCanvas(data.width, data.height);
        }
    });

    EventBus.on('PSD_LOADED', (data: any) => {
        renderLayers();
        updateLayerPanel();
        if (window.resizeCanvas && data.width && data.height) {
            window.resizeCanvas(data.width, data.height);
        }
    });

    // ─── History & Document Signals ──────────────────────────

    EventBus.on('HISTORY_CHANGED', () => {
        if (window.updateHistoryUI) {
            window.updateHistoryUI();
        }
    });

    // Generic push — any coordinator can emit this with an action that has .undo()/.redo()
    EventBus.on('PUSH_HISTORY', (action: any) => {
        if (action && typeof action.undo === 'function') {
            historyManager.push(action);
        }
    });

    // [CRITICAL_LOGIC_START]
    EventBus.on('DOCUMENT_SWITCHED', () => {
        const activeDocument = g.documents[g.activeDocumentIndex];
        if (activeDocument && window.resizeCanvas) {
            window.resizeCanvas(activeDocument.width, activeDocument.height);
        }

        // ✨ BUG FIX #41: Synchronize the global 'layers' singleton with the document's layers.
        // Importing modules see the global singleton 'layers', so we MUST update it.
        const globalLayersList = window.layers;
        if (activeDocument && activeDocument.layers && globalLayersList) {
            globalLayersList.splice(0, globalLayersList.length, ...activeDocument.layers);
            g.activeLayerIndex = activeDocument.activeLayerIndex ?? 0;
        }

        // #2064: Swap window.historyManager to the active document's isolated history.
        // All canvas-ui code pushes to window.historyManager, so swapping it here
        // gives each document its own independent undo stack.
        if (activeDocument) {
            const docHistory = getHistoryForDoc((activeDocument as any).id || String(g.activeDocumentIndex));
            (window as any).historyManager = docHistory;
            EventBus.emit('HISTORY_CHANGED');
        }

        renderLayers();
        updateLayerPanel();
    });
    // [CRITICAL_LOGIC_END]

    EventBus.on('DOCUMENT_TABS_CHANGED', () => {
        if (window.renderImageTabs) {
            window.renderImageTabs();
        }

        // BUG FIX #31: Show "New Image" dialog if no documents left
        if (g.documents.length === 0) {
            if (window.openNewImageDialog) {
                window.openNewImageDialog();
            }
        }

        // #2064: Ensure the active document has its own history when a new doc is created
        const activeDoc = g.documents[g.activeDocumentIndex] as any;
        if (activeDoc && (window as any).historyManager !== getHistoryForDoc(activeDoc.id || String(g.activeDocumentIndex))) {
            const docHistory = getHistoryForDoc(activeDoc.id || String(g.activeDocumentIndex));
            (window as any).historyManager = docHistory;
        }
    });

    // ─── Tool Signals ────────────────────────────────────────

    EventBus.on('TOOL_CHANGED', (data: { toolId: string }) => {
        window.dispatchEvent(new CustomEvent('toolChanged', { detail: { tool: data.toolId } }));
    });

    EventBus.on('OPEN_IMAGE_PATH', async (data: { path: string }) => {
        try {
            console.log(`[UI-Coordinator] Attempting to open path: ${data.path}`);
            
            // Replaced tauri-plugin-fs with robust PlatformService (Rust base)
            const buffer = await PlatformService.readFile(data.path);
            
            if (!buffer || buffer.byteLength === 0) {
                throw new Error('Dosya boş veya okunamadı.');
            }

            // Trigger the correct buffer loading routine via window exposure
            if ((window as any).importImageFromBuffer) {
                (window as any).importImageFromBuffer(buffer, data.path.split(/[/\\]/).pop(), data.path);
            } else {
                console.error('[UI-Bridge] importImageFromBuffer not found on window!');
                SHOW_TOAST('Hata: Görüntü yükleme servisi hazır değil.', 'error');
            }
        } catch (error: any) {
            console.error('[UI-Coordinator] Failed to open image from path:', error);
            // Show detailed toast to user (#2097-Fix)
            const toastText = `Hata: ${error.message || 'Dosya açılamadı'}. Yol: ${data.path}`;
            EventBus.emit('SHOW_TOAST', { text: toastText, duration: 5000 });
        }
    });

    // ─── Startup Logic ───────────────────────────────────────

    // BUG FIX #31: Initial 500px transparent canvas if empty
    setTimeout(() => {
        if (g.documents.length === 0) {
            console.log('[UI-Coordinator] startup: creating default 500x500 document.');
            
            const widthInput = document.getElementById('newWidth') as HTMLInputElement;
            const heightInput = document.getElementById('newHeight') as HTMLInputElement;
            const transparentBg = document.getElementById('transparentBg') as HTMLInputElement;
            
            if (widthInput) widthInput.value = '500';
            if (heightInput) heightInput.value = '500';
            if (transparentBg) transparentBg.checked = true;
            
            if (window.createNewImage) {
                window.createNewImage();
            }
        }
    }, 500);

    // ─── Bridge Functions ────────────────────────────────────
    (window as any).toggleViewerMode = () => {
        EventBus.emit('TOGGLE_VIEWER_MODE');
    };
    (window as any).toggleViewerEditMode = () => {
        // We can't emit directly to the private toggleEditMode, 
        // but we can emit a signal that ViewerCoordinator listens to.
        EventBus.emit('TOGGLE_VIEWER_EDIT_MODE');
    };
}
